import { difference } from 'lodash-es';
import { batch } from 'react-redux';
import { hideNotification, showNotification } from '../../notifications/actions';
import { NOTIFICATION_TIMEOUT_TYPE, RAISE_HAND_NOTIFICATION_ID } from '../../notifications/constants';
import { getCurrentConference } from '../conference/functions';
import { getDisableNextSpeakerNotification, getSsrcRewritingFeatureFlag, hasBeenNotified, isNextToSpeak } from '../config/functions.any';
import { MEDIA_TYPE, VIDEO_TYPE } from '../media/constants';
import StateListenerRegistry from '../redux/StateListenerRegistry';
import { NOTIFIED_TO_SPEAK } from './actionTypes';
import { createVirtualScreenshareParticipant, participantLeft } from './actions';
import { getParticipantById, getRemoteScreensharesBasedOnPresence, getVirtualScreenshareParticipantOwnerId } from './functions';
import { FakeParticipant } from './types';
StateListenerRegistry.register(
/* selector */ state => state['features/base/tracks'], 
/* listener */ (tracks, store) => _updateScreenshareParticipants(store));
StateListenerRegistry.register(
/* selector */ state => state['features/base/participants'].remoteVideoSources, 
/* listener */ (remoteVideoSources, store) => getSsrcRewritingFeatureFlag(store.getState())
    && _updateScreenshareParticipantsBasedOnPresence(store));
StateListenerRegistry.register(
/* selector */ state => state['features/base/participants'].raisedHandsQueue, 
/* listener */ (raisedHandsQueue, store) => {
    if (raisedHandsQueue.length
        && isNextToSpeak(store.getState())
        && !hasBeenNotified(store.getState())
        && !getDisableNextSpeakerNotification(store.getState())
        && !store.getState()['features/visitors'].iAmVisitor) { // visitors raise hand to be promoted
        _notifyNextSpeakerInRaisedHandQueue(store);
    }
    if (!raisedHandsQueue[0]) {
        store.dispatch(hideNotification(RAISE_HAND_NOTIFICATION_ID));
    }
});
/**
 * Compares the old and new screenshare lists provided and creates/removes the virtual screenshare participant
 * tiles accordingly.
 *
 * @param {Array<string>} oldScreenshareSourceNames - List of old screenshare source names.
 * @param {Array<string>} newScreenshareSourceNames - Current list of screenshare source names.
 * @param {Object} store - The redux store.
 * @returns {void}
 */
function _createOrRemoveVirtualParticipants(oldScreenshareSourceNames, newScreenshareSourceNames, store) {
    const { dispatch, getState } = store;
    const conference = getCurrentConference(getState());
    const removedScreenshareSourceNames = difference(oldScreenshareSourceNames, newScreenshareSourceNames);
    const addedScreenshareSourceNames = difference(newScreenshareSourceNames, oldScreenshareSourceNames);
    if (removedScreenshareSourceNames.length) {
        removedScreenshareSourceNames.forEach(id => dispatch(participantLeft(id, conference, {
            fakeParticipant: FakeParticipant.RemoteScreenShare
        })));
    }
    if (addedScreenshareSourceNames.length) {
        addedScreenshareSourceNames.forEach(id => dispatch(createVirtualScreenshareParticipant(id, false, conference)));
    }
}
/**
 * Handles creating and removing virtual screenshare participants.
 *
 * @param {*} store - The redux store.
 * @returns {void}
 */
function _updateScreenshareParticipants(store) {
    const { dispatch, getState } = store;
    const state = getState();
    const conference = getCurrentConference(state);
    const { sortedRemoteVirtualScreenshareParticipants, localScreenShare } = state['features/base/participants'];
    const previousScreenshareSourceNames = [...sortedRemoteVirtualScreenshareParticipants.keys()];
    const { localSourceName: newLocalSceenshareSourceName, remoteSourceNames: currentScreenshareSourceNames } = _getScreenshareSourceNamesFromTracks(state);
    if (!localScreenShare && newLocalSceenshareSourceName) {
        dispatch(createVirtualScreenshareParticipant(newLocalSceenshareSourceName, true, conference));
    }
    if (localScreenShare && !newLocalSceenshareSourceName) {
        dispatch(participantLeft(localScreenShare.id, conference, {
            fakeParticipant: FakeParticipant.LocalScreenShare
        }));
    }
    if (getSsrcRewritingFeatureFlag(state)) {
        const currentPresenceScreenshareSourceNames = getRemoteScreensharesBasedOnPresence(state) ?? [];
        const currentSsrcRewritingScreenshareSourceNames = Array.from(new Set([
            ...currentPresenceScreenshareSourceNames,
            ...currentScreenshareSourceNames
        ]));
        _createOrRemoveVirtualParticipants(previousScreenshareSourceNames, currentSsrcRewritingScreenshareSourceNames, store);
        return;
    }
    _createOrRemoveVirtualParticipants(previousScreenshareSourceNames, currentScreenshareSourceNames, store);
}
/**
 * Handles the creation and removal of remote virtual screenshare participants when ssrc-rewriting is enabled.
 *
 * @param {Object} store - The redux store.
 * @returns {void}
 */
function _updateScreenshareParticipantsBasedOnPresence(store) {
    const { getState } = store;
    const state = getState();
    const { sortedRemoteVirtualScreenshareParticipants } = state['features/base/participants'];
    const previousScreenshareSourceNames = [...sortedRemoteVirtualScreenshareParticipants.keys()];
    const presenceScreenshareSourceNames = getRemoteScreensharesBasedOnPresence(state) ?? [];
    const { remoteSourceNames: trackScreenshareSourceNames } = _getScreenshareSourceNamesFromTracks(state);
    const currentScreenshareSourceNames = Array.from(new Set([
        ...presenceScreenshareSourceNames,
        ...trackScreenshareSourceNames
    ]));
    _createOrRemoveVirtualParticipants(previousScreenshareSourceNames, currentScreenshareSourceNames, store);
}
/**
 * Returns the active local and remote screenshare source names represented by current tracks.
 * Keeping the remote track sources as a fallback prevents transient presence updates during
 * E2EE media-session restarts from removing a screenshare that is still active.
 *
 * @param {IReduxState} state - The current Redux state.
 * @returns {Object}
 */
function _getScreenshareSourceNamesFromTracks(state) {
    let localSourceName;
    const remoteSourceNames = [];
    state['features/base/tracks'].forEach(track => {
        const isScreenshareTrack = track.mediaType === MEDIA_TYPE.SCREENSHARE
            || track.videoType === VIDEO_TYPE.DESKTOP;
        if (!track.jitsiTrack || !isScreenshareTrack || track.jitsiTrack.isMuted()) {
            return;
        }
        const sourceName = track.jitsiTrack.getSourceName();
        if (!sourceName) {
            return;
        }
        if (track.local) {
            localSourceName = sourceName;
        }
        else if (getParticipantById(state, getVirtualScreenshareParticipantOwnerId(sourceName))) {
            remoteSourceNames.push(sourceName);
        }
    });
    return {
        localSourceName,
        remoteSourceNames
    };
}
/**
 * Handles notifying the next speaker in the raised hand queue.
 *
 * @param {*} store - The redux store.
 * @returns {void}
 */
function _notifyNextSpeakerInRaisedHandQueue(store) {
    const { dispatch } = store;
    batch(() => {
        dispatch(showNotification({
            titleKey: 'notify.nextToSpeak',
            maxLines: 2
        }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
        dispatch({
            type: NOTIFIED_TO_SPEAK
        });
    });
}
